import { ref, watch } from 'vue';
import { usePedidoStore } from '@/stores/pedido';
import { useAuthStore } from '@/stores/auth';
import usePedidoActions from './usePedidoActions';

// Create a single instance of state that all components can share
const deactivateModalOpen = ref(false);
const selectedRevenda = ref(null);
const editOrderModalOpen = ref(false);
const selectedPedido = ref(null);
const editedPedido = ref({});
const editOrderLoading = ref(false);
const observationModalOpen = ref(false);
const observationData = ref({
  type: null,
  pedido: null,
  text: ''
});

// Adicionar novo estado para modal de assistência
const assistanceModalOpen = ref(false);
const assistanceData = ref({
  pedido: null,
  idrevenda: '',
  motivo: ''
});
const assistanceLoading = ref(false);

// Register modal in global counter
const registerModalOpen = () => {
  if (window.pedidoModalOpenCount === undefined) {
    window.pedidoModalOpenCount = 0;
  }
  window.pedidoModalOpenCount++;
};

// Unregister modal from global counter
const unregisterModalOpen = () => {
  if (window.pedidoModalOpenCount) {
    window.pedidoModalOpenCount--;
  }
};

export default function useModalManagement() {
  const pedidoStore = usePedidoStore();
  const authStore = useAuthStore();
  const { showNotification, showErrorNotification } = usePedidoActions();
  
  const cancelDeactivation = () => {
    deactivateModalOpen.value = false;
    selectedRevenda.value = null;
    unregisterModalOpen();
  };

  // Modal handlers for Order
  const openEditOrderModal = (pedido) => {
    if (!pedido) return;
    
    selectedPedido.value = pedido;
    editedPedido.value = {
      id: pedido.id,
      dtprevisao: pedido.dtprevisao ? pedido.dtprevisao.split(' ')[0] : '',
      horaprevisao: pedido.dtprevisao ? pedido.dtprevisao.split(' ')[1]?.substring(0, 5) || '' : '',
      numero: pedido.numero_entrega || '',
      complemento: pedido.complemento_entrega || '',
      vlpedido: pedido.vlpedido || 0,
      qtd_parcela: pedido.qtd_parcela || 1,
      vl_parcela: pedido.vl_parcela || 0,
      idformapagamento: pedido.idformapagamento || 1,
      troco: pedido.troco || 0,
      quantidade: pedido.quantidade || 1
    };
    
    editOrderModalOpen.value = true;
    registerModalOpen(); // Register modal as open
  };

  const saveEditedOrder = async () => {
    if (!selectedPedido.value) return;
    
    editOrderLoading.value = true;
    
    try {
      const orderData = {
        dtprevisao: editedPedido.value.dtprevisao && editedPedido.value.horaprevisao
          ? `${editedPedido.value.dtprevisao} ${editedPedido.value.horaprevisao}:00`
          : null,
        numero_entrega: editedPedido.value.numero,
        complemento_entrega: editedPedido.value.complemento,
        vlpedido: Number(editedPedido.value.vlpedido),
        qtd_parcela: Number(editedPedido.value.qtd_parcela),
        vl_parcela: Number(editedPedido.value.vl_parcela),
        idformapagamento: Number(editedPedido.value.idformapagamento),
        troco: Number(editedPedido.value.troco),
        quantidade: Number(editedPedido.value.quantidade)
      };
      
      const response = await pedidoStore.editarPedido(selectedPedido.value.id, orderData);
      
      if (response.success) {
        Object.assign(selectedPedido.value, response.data || orderData);
        editOrderModalOpen.value = false;
        selectedPedido.value = null;
        showNotification(response.success ? 'Pedido atualizado com sucesso!' : 'Erro ao atualizar pedido!');
      }
    } catch (error) {
      showErrorNotification('Erro ao atualizar pedido!');
    } finally {
      editOrderLoading.value = false;
      unregisterModalOpen();
    }
  };

  const cancelOrderEdit = () => {
    editOrderModalOpen.value = false;
    selectedPedido.value = null;
    editedPedido.value = {};
    unregisterModalOpen();
  };

  // Modal handlers for Observation
  const openObservationModal = (pedido, type) => {
    if (!pedido) return;
    
    let currentText = '';
    switch (type) {
      case 'pedido':
        currentText = pedido.observacao || '';
        break;
      case 'revenda':
        currentText = pedido.revenda?.revendedor?.obsboasvindas || '';
        break;
      case 'aplicativo':
        currentText = pedido.observacao_aplicativo || '';
        break;
    }
    
    observationData.value = { type, pedido, text: currentText };
    observationModalOpen.value = true;
    registerModalOpen(); // Register modal as open
  };

  const salvarObservacao = async () => {
    const { type, pedido, text } = observationData.value;
    if (!pedido) return;
    
    try {
      let response;
      
      switch (type) {
        case 'pedido':
          response = await pedidoStore.atualizarObservacaoPedido(pedido.id, text);
          if (response.success) {
            const estaEmColunaSuspeitos = pedido.suspeito === 1;
            pedido.observacao = text;
            pedido.atendente_id_obs = authStore.user?.id;
            pedido.suspeito = 0;
            
            // Se o pedido estava na coluna de suspeitos, vamos removê-lo
            if (estaEmColunaSuspeitos) {
              // Remover o pedido da coluna de suspeitos
              const suspeitosData = pedidoStore.getPedidosByStatus('suspeitos');
              const index = suspeitosData.findIndex(p => p.id === pedido.id);
              if (index !== -1) {
                // Remover o pedido da lista (usando splice para manter a reatividade)
                pedidoStore.removerPedidoDaColuna('suspeitos', pedido.id);
              }
            }
          }
          break;
        case 'revenda':
          if (!pedido.revenda?.revendedor) return;
          response = await pedidoStore.atualizarObsBoasVindas(pedido.revenda.revendedor.id, text);
          if (response.success) {
            pedido.revenda.revendedor.obsboasvindas = text;
          }
          break;
        case 'aplicativo':
          response = await pedidoStore.atualizarObservacaoAplicativo(pedido.id, text);
          if (response.success) {
            const estaEmColunaSuspeitos = pedido.suspeito === 1;
            pedido.observacao_aplicativo = text;
            pedido.suspeito = 0;
            
            // Se o pedido estava na coluna de suspeitos, vamos removê-lo
            if (estaEmColunaSuspeitos) {
              // Remover o pedido da coluna de suspeitos
              const suspeitosData = pedidoStore.getPedidosByStatus('suspeitos');
              const index = suspeitosData.findIndex(p => p.id === pedido.id);
              if (index !== -1) {
                // Remover o pedido da lista (usando splice para manter a reatividade)
                pedidoStore.removerPedidoDaColuna('suspeitos', pedido.id);
              }
            }
          }
          break;
      }
      
      showNotification(response.message);
      observationModalOpen.value = false;
    } catch (error) {
      showErrorNotification('Erro ao salvar observação!');
    } finally {
      unregisterModalOpen();
    }
  };

  const closeObservationModal = () => {
    observationModalOpen.value = false;
    observationData.value = { type: null, pedido: null, text: '' };
    unregisterModalOpen();
  };

  // Modal handlers for Assistance
  const openAssistanceModal = (pedido) => {
    if (!pedido) {
      console.warn('Pedido é null ou undefined');
      return;
    }
    
    // Certifique-se de que o pedido tem a propriedade revenda
    const revendaId = pedido.revenda?.id || '';
    
    assistanceData.value = {
      pedido,
      idrevenda: revendaId,
      motivo: ''
    };
    
    assistanceModalOpen.value = true;
    registerModalOpen(); // Register modal as open
  };

  const solicitarAssistencia = async () => {
    const { pedido, idrevenda, motivo } = assistanceData.value;
    if (!pedido || !idrevenda || !motivo.trim()) {
      showNotification('Preencha todos os campos para solicitar assistência');
      return;
    }
    
    assistanceLoading.value = true;
    
    try {
      const response = await pedidoStore.solicitarAssistencia(pedido.id, {
        idrevenda: Number(idrevenda),
        motivo
      });
      
      // Check if the response indicates success
      if (response.success) {
        showNotification(response.message);
        assistanceModalOpen.value = false;
        // Limpar o formulário
        assistanceData.value = {
          pedido: null,
          idrevenda: '',
          motivo: ''
        };
      } else {
        // Handle error response from the store
        // Check for validation errors in the response
        if (response.error && response.error.response && response.error.response.data) {
          const errorData = response.error.response.data;
          
          if (errorData.errors) {
            // Format validation error messages
            let errorMessage = 'Erro de validação:\n';
            Object.keys(errorData.errors).forEach(field => {
              errorData.errors[field].forEach(message => {
                errorMessage += ` ${message}\n`;
              });
            });
            showErrorNotification(errorMessage.trim());
          } else {
            // Show general error message
            showErrorNotification(errorData.message || response.message || 'Erro ao solicitar assistência');
          }
        } else {
          // Fallback error message
          showErrorNotification(response.message || 'Erro ao solicitar assistência');
        }
      }
    } catch (error) {
      showErrorNotification('Erro ao solicitar assistência');
    } finally {
      assistanceLoading.value = false;
      unregisterModalOpen();
    }
  };

  const cancelAssistance = () => {
    assistanceModalOpen.value = false;
    assistanceData.value = {
      pedido: null,
      idrevenda: '',
      motivo: ''
    };
    unregisterModalOpen();
  };

  // Add a method to close all open modals
  const closeAllModals = () => {
    // Close each possible modal by setting its state to false
    editOrderModalOpen.value = false;
    observationModalOpen.value = false;
    assistanceModalOpen.value = false;
    deactivateModalOpen.value = false;
    // Also reset any modal data
    selectedPedido.value = null;
    selectedRevenda.value = null;
    editedPedido.value = {};
    observationData.value = { type: null, pedido: null, text: '' };
    assistanceData.value = { pedido: null, idrevenda: '', motivo: '' };
    
    // Make sure to unregister all modals
    if (window.pedidoModalOpenCount > 0) {
      window.pedidoModalOpenCount = 0;
    }
  };

  // Add watchers for all modals to manage register/unregister
  watch(editOrderModalOpen, (isOpen) => {
    if (!isOpen) unregisterModalOpen();
  });
  
  watch(observationModalOpen, (isOpen) => {
    if (!isOpen) unregisterModalOpen();
  });
  
  watch(deactivateModalOpen, (isOpen) => {
    if (!isOpen) unregisterModalOpen();
  });
  
  watch(assistanceModalOpen, (isOpen) => {
    if (!isOpen) unregisterModalOpen();
  });

  return {
    // Estados
    deactivateModalOpen,
    selectedRevenda,
    editOrderModalOpen,
    selectedPedido,
    editedPedido,
    editOrderLoading,
    observationModalOpen,
    observationData,
    assistanceModalOpen,
    assistanceData,
    assistanceLoading,
    
    // Métodos
    cancelDeactivation,
    openEditOrderModal,
    saveEditedOrder,
    cancelOrderEdit,
    openObservationModal,
    salvarObservacao,
    closeObservationModal,
    openAssistanceModal,
    solicitarAssistencia,
    cancelAssistance,
    closeAllModals,
    
    // For testing and external access
    registerModalOpen,
    unregisterModalOpen
  };
}
